/**
 * SupportInboxPanel — `/admin/support-inbox`
 *
 * Two-pane layout (list on left, conversation on right).
 *   • Left: latest inbound emails, filter by status, search across subject/
 *     preview/sender, unread badges. Auto-refresh every 30s so replies
 *     land without a page reload.
 *   • Right: the entire conversation as a chat-style transcript (inbound
 *     bubbles left / outbound right) + inline attachment chips and a
 *     Reply composer that sends via Resend outbound and threads the sent
 *     message back onto the same conversation.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Loader2, RefreshCw, Search, Inbox, Mail, MailOpen, Archive,
  Send, Paperclip, ArrowLeft,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

const STATUS_LABELS = { unread: 'Unread', read: 'Read', archived: 'Archived' };

function StatChip({ label, value, tone = 'slate' }) {
  return (
    <div className={`rounded-2xl border border-border bg-background px-4 py-3 flex items-center gap-3`}>
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl bg-${tone}-100 text-${tone}-700 dark:bg-${tone}-500/15 dark:text-${tone}-300`}>
        <Inbox size={16} />
      </span>
      <div>
        <p className="text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground leading-none">{label}</p>
        <p className="mt-1 text-[22px] font-bold text-foreground tabular-nums leading-none">{value}</p>
      </div>
    </div>
  );
}

function AttachmentChip({ msgId, att }) {
  const label = att.filename || 'attachment';
  const skipped = att.skipped || !att.content_type;
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/support-inbox/${msgId}/attachments/${att.id || att.filename}`;
  const token = typeof window !== 'undefined' ? localStorage.getItem('le_token') : null;

  const download = async () => {
    if (skipped) {
      toast.error('This attachment was too large — Resend keeps it for 7 days, ask the sender to resend or fetch via the Resend dashboard.');
      return;
    }
    try {
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = label;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e?.message || 'Attachment download failed');
    }
  };

  return (
    <button
      type="button"
      onClick={download}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11.5px] font-medium ${
        skipped ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-border bg-background text-foreground hover:bg-muted'
      }`}
      data-testid={`inbox-attachment-${att.id || att.filename}`}
    >
      <Paperclip size={11} />
      <span className="max-w-[180px] truncate">{label}</span>
      {att.size ? <span className="text-muted-foreground">· {(att.size / 1024).toFixed(0)} KB</span> : null}
      {skipped ? <span className="text-amber-700">· too large</span> : null}
    </button>
  );
}

function MessageBubble({ msg }) {
  const outbound = msg.direction === 'outbound';
  const align = outbound ? 'ml-auto' : 'mr-auto';
  const bg = outbound ? 'bg-sky-500/10 border-sky-500/30 text-foreground' : 'bg-background border-border text-foreground';
  const time = msg.received_at ? formatDistanceToNow(new Date(msg.received_at), { addSuffix: true }) : '';
  const safeHtml = useMemo(() => {
    if (msg.html) return DOMPurify.sanitize(msg.html, { USE_PROFILES: { html: true } });
    return null;
  }, [msg.html]);
  return (
    <div className={`max-w-[92%] ${align} rounded-2xl border ${bg} p-4 space-y-2`} data-testid={`inbox-bubble-${msg.id}`}>
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${outbound ? 'bg-sky-500' : 'bg-emerald-500'}`} />
        <span className="font-semibold text-foreground">{outbound ? (msg.from_name || 'Blayzrr Support') : (msg.from_name || msg.from_email)}</span>
        <span>·</span>
        <span>{time}</span>
        {msg.subject && (
          <>
            <span>·</span>
            <span className="italic truncate max-w-[280px]">{msg.subject}</span>
          </>
        )}
      </div>
      {safeHtml ? (
        <div className="prose prose-sm max-w-none text-[13.5px] leading-[1.55] break-words" dangerouslySetInnerHTML={{ __html: safeHtml }} />
      ) : (
        <p className="text-[13.5px] leading-[1.55] whitespace-pre-wrap break-words">{msg.text || msg.preview || <em className="text-muted-foreground">Body still fetching…</em>}</p>
      )}
      {(msg.attachments || []).length > 0 && (
        <div className="pt-1 flex flex-wrap gap-1.5">
          {(msg.attachments || []).map((a, i) => (
            <AttachmentChip key={a.id || i} msgId={msg.id} att={a} />
          ))}
        </div>
      )}
    </div>
  );
}

function ThreadView({ inboxHead, onBack, onStatusChanged }) {
  const [loading, setLoading] = useState(true);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadThread = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.listSupportInbox({ conversation: inboxHead.conversation_key, limit: 200 });
      setThread(data?.items || []);
      // Mark the head message read once we've opened the thread.
      if (inboxHead.status === 'unread') {
        try {
          await adminApi.patchSupportInbox(inboxHead.id, { status: 'read' });
          onStatusChanged?.();
        } catch { /* non-fatal */ }
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load conversation'));
    } finally {
      setLoading(false);
    }
  }, [inboxHead, onStatusChanged]);

  useEffect(() => { loadThread(); }, [loadThread]);

  useEffect(() => {
    // Autoscroll to the latest message when the thread loads/updates.
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const sendReply = async () => {
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    try {
      await adminApi.replySupportInbox(inboxHead.id, { body_text: body });
      setReply('');
      toast.success(`Reply sent to ${inboxHead.from_email}`);
      await loadThread();
      onStatusChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Reply send failed'));
    } finally {
      setSending(false);
    }
  };

  const archive = async () => {
    try {
      await adminApi.patchSupportInbox(inboxHead.id, { status: 'archived' });
      toast.success('Archived.');
      onStatusChanged?.();
      onBack?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Archive failed'));
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-260px)] min-h-[520px] rounded-xl border border-border bg-background" data-testid="inbox-thread-view">
      <div className="flex items-center justify-between border-b border-border p-3">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack} className="lg:hidden" data-testid="inbox-thread-back">
            <ArrowLeft size={14} />
          </Button>
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold truncate">{inboxHead.subject || '(no subject)'}</p>
            <p className="text-[11.5px] text-muted-foreground truncate">
              With <span className="text-foreground font-medium">{inboxHead.from_name || inboxHead.from_email}</span>
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={archive} className="gap-1 text-[11.5px]" data-testid="inbox-thread-archive">
          <Archive size={13} /> Archive
        </Button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && (
          <p className="text-center text-muted-foreground py-8"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</p>
        )}
        {!loading && thread.length === 0 && (
          <p className="text-center text-muted-foreground py-8">Nothing yet in this conversation.</p>
        )}
        {!loading && thread.map((m) => <MessageBubble key={m.id} msg={m} />)}
      </div>

      <div className="border-t border-border p-3 space-y-2 bg-muted/30">
        <Textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder={`Reply to ${inboxHead.from_email}…`}
          rows={4}
          disabled={sending}
          maxLength={20000}
          className="resize-none"
          data-testid="inbox-reply-textarea"
        />
        <div className="flex items-center justify-between">
          <p className="text-[10.5px] text-muted-foreground">Sent via Resend · From <span className="text-foreground font-mono">help@blayzrr.com</span></p>
          <Button
            onClick={sendReply}
            disabled={sending || !reply.trim()}
            className="gap-1.5"
            data-testid="inbox-reply-send"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? 'Sending…' : 'Send reply'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SupportInboxPanel() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ unread: 0, read: 0, archived: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const data = await adminApi.listSupportInbox(params);
      // Keep only INBOUND messages in the left list (outbound sits in the thread view).
      const inbounds = (data?.items || []).filter((m) => m.direction === 'inbound');
      setRows(inbounds);
      setStats(data?.stats || { unread: 0, read: 0, archived: 0, total: 0 });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load inbox'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  // Poll every 30s so replies land without a manual refresh.
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-support-inbox-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip label="Unread"   value={stats.unread}   tone="emerald" />
        <StatChip label="Read"     value={stats.read}     tone="sky" />
        <StatChip label="Archived" value={stats.archived} tone="slate" />
        <StatChip label="Total"    value={stats.total}    tone="amber" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search sender, subject or preview…"
            className="pl-9 h-10"
            data-testid="inbox-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-10" data-testid="inbox-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="unread">Unread</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} disabled={loading} className="h-10 gap-1.5" data-testid="inbox-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        <div className="rounded-xl border border-border bg-background overflow-hidden">
          {loading && rows.length === 0 && (
            <p className="text-center text-muted-foreground py-8"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</p>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-6 text-center" data-testid="inbox-empty">
              <Inbox size={20} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-[13.5px] font-semibold text-foreground">No support emails yet.</p>
              <p className="text-[12px] text-muted-foreground mt-1">
                Point <b>help@blayzrr.com</b> at Resend Inbound and set the webhook URL to <code className="text-[11px] bg-muted px-1 py-0.5 rounded">/api/inbound/resend</code>.
              </p>
            </div>
          )}
          <ul className="divide-y divide-border max-h-[calc(100vh-320px)] overflow-y-auto">
            {rows.map((r) => {
              const active = selected?.id === r.id;
              const isUnread = r.status === 'unread';
              const StatusIcon = r.status === 'archived' ? Archive : (isUnread ? Mail : MailOpen);
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(r)}
                    className={`w-full text-left p-3 flex gap-2.5 transition ${active ? 'bg-sky-500/10' : 'hover:bg-muted/50'}`}
                    data-testid={`inbox-row-${r.id}`}
                  >
                    <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${isUnread ? 'bg-emerald-500' : 'bg-transparent'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <StatusIcon size={12} className="text-muted-foreground shrink-0" />
                        <p className={`text-[13px] truncate ${isUnread ? 'font-bold text-foreground' : 'font-medium text-foreground'}`}>{r.from_name || r.from_email}</p>
                        <span className="ml-auto text-[10.5px] text-muted-foreground shrink-0">
                          {r.received_at ? format(new Date(r.received_at), 'd MMM') : ''}
                        </span>
                      </div>
                      <p className={`text-[12.5px] truncate ${isUnread ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                        {r.subject || '(no subject)'}
                      </p>
                      {r.preview && (
                        <p className="text-[11.5px] text-muted-foreground truncate">{r.preview}</p>
                      )}
                      {r.reply_count > 0 && (
                        <p className="mt-1 text-[10.5px] text-sky-700 dark:text-sky-400">↳ {r.reply_count} repl{r.reply_count === 1 ? 'y' : 'ies'} sent</p>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          {selected ? (
            <ThreadView
              inboxHead={selected}
              onBack={() => setSelected(null)}
              onStatusChanged={load}
            />
          ) : (
            <div className="h-full rounded-xl border border-dashed border-border p-10 text-center flex flex-col items-center justify-center min-h-[420px]" data-testid="inbox-empty-thread">
              <Inbox size={22} className="text-muted-foreground mb-2" />
              <p className="text-[13.5px] font-semibold text-foreground">Pick a conversation on the left.</p>
              <p className="text-[12px] text-muted-foreground mt-1 max-w-sm">Threads group inbound and outbound emails so you can see the whole back-and-forth in one place.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupportInboxPanel;
